<template>
    <div class="widget-date-picker">
        <van-field readonly border :error="false" :label="label" :rules="formRules">
            <template #input>
                <div class="field-input">
                    <div class="field-button" @click.stop="clickPicker(true)" :class="{ 'no-date': !startDateText }">
                        {{ startDateShowText }}
                    </div>
                    <span>-</span>
                    <div class="field-button" @click.stop="clickPicker(false)" :class="{ 'no-date': !endDateText }">
                        {{ endDateShowText }}
                    </div>
                </div>
            </template>
        </van-field>
        <van-popup v-model="showPicker" round position="bottom">
            <van-datetime-picker
                show-toolbar
                :title="dateTimeTitle"
                :type="dateTimeType"
                :min-hour="minHour"
                :max-hour="maxHour"
                :min-minute="minMinute"
                :max-minute="maxMinute"
                :formatter="formatter"
                @cancel="showPicker = false"
                @confirm="onConfirm"
            />
        </van-popup>
    </div>
</template>

<script>
import Vue from "vue";
import i18n from "@/locales/i18n";
import { datetimeFormat } from "@/utils/lang";
import moment from "moment";

export default Vue.extend({
    name: "DateRangeTimePicker",
    props: {
        label: {
            type: String,
            required: true
        },
        dateTimeTitle: String,
        dateTimeType: {
            type: String,
            default: "date"
        },
        minHour: {
            type: Number,
            default: 0
        },
        maxHour: {
            type: Number,
            default: 23
        },
        minMinute: {
            type: Number,
            default: 0
        },
        maxMinute: {
            type: Number,
            default: 59
        },
        formatter: {
            type: Function,
            default: function(type, val) {
                switch (type) {
                    case "year":
                        return val + i18n.t("common.date.year");
                    case "month":
                        return val + i18n.t("common.date.month");
                    case "day":
                        return val + i18n.t("common.date.day");
                    case "hour":
                        return val + i18n.t("common.date.hour");
                    case "minute":
                        return val + i18n.t("common.date.minute");
                    default:
                        return val;
                }
            }
        },
        startDateText: {
            type: String,
            default: ""
        },
        endDateText: {
            type: String,
            default: ""
        },
        readOnly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showPicker: false,
            isStartDate: true,
            formRules: [
                { validator: this.validator, message: this.$t("common.error.required") },
                { validator: this.validatorDate, message: this.$t("endDateIsAfterStartDate") }
            ]
        };
    },
    methods: {
        validator() {
            return !!this.startDateText && !!this.endDateText;
        },
        validatorDate() {
            if (!!this.startDateText && !!this.endDateText) {
                return !moment(this.startDateText).isAfter(moment(this.endDateText));
            }
            return null;
        },
        clickPicker(isStartDate) {
            if (this.readOnly) {
                return;
            }
            this.isStartDate = isStartDate;
            this.showPicker = true;
        },
        onConfirm(val) {
            this.showPicker = false;
            if (this.isStartDate) {
                return this.$emit("change", [datetimeFormat(val, this.$constants.DATE_FMT), this.endDateText]);
            }
            return this.$emit("change", [this.startDateText, datetimeFormat(val, this.$constants.DATE_FMT)]);
        }
    },
    computed: {
        startDateShowText() {
            return this.startDateText ? this.startDateText : this.$t("registration.startDateTime");
        },
        endDateShowText() {
            return this.endDateText ? this.endDateText : this.$t("registration.endDateTime");
        }
    }
});
</script>

<style scoped lang="less">
/deep/ .van-field__label {
    margin-right: 0;
}
.widget-date-picker {
    /deep/ .van-cell {
        display: flex;
        align-items: center;
    }
    .van-cell:last-child::after,
    .van-cell--borderless::after {
        display: block;
    }
    .field-input {
        display: flex;
        gap: 4px;
        align-items: center;
        .field-button {
            overflow: hidden;
            color: #3c3e46;
            text-align: right;
            text-overflow: ellipsis;
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 22px;
            border-radius: 100px;
            background: #f6f7f9;
            padding: 9px 14px;
            gap: 10px;
            position: relative;
            display: inline-block;
            box-sizing: border-box;
            margin: 0;

            &.no-date {
                color: #b1b5bf;
            }
        }
    }
}
</style>
